import { selectCommunity } from 'core/selectors/communities';
import { useServices } from 'core/services';
import { BaseHttpError } from 'core/services/api/errors';
import { CommunitiesResponse } from 'core/services/api/response-types';
import { Community } from 'core/types';
import { communityKeys } from 'core/utils/query-key-factory';
import { InfiniteData, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

export const useGetCommunity = (
	id: string,
	options: { enabled: boolean } = { enabled: true }
) => {
	const { api, authentication, queryClient } = useServices();
	const isLoggedIn = authentication.isAuthenticated();

	const { data, isSuccess, isLoading, isError } = useQuery<
		Community,
		BaseHttpError
	>({
		queryKey: communityKeys.detail(id),
		queryFn: () => api.getCommunity(id).then(response => response.data),
		enabled: !!id && isLoggedIn && options.enabled,
		select: selectCommunity,
		placeholderData: previousData => previousData,
		refetchOnWindowFocus: true,
	});

	// Handle onSuccess logic (moved from query options in v5)
	useEffect(() => {
		if (data) {
			const currentCommunities = queryClient.getQueryData<
				InfiniteData<CommunitiesResponse>
			>(communityKeys.list('my_communities'));

			if (currentCommunities) {
				const communityIsFirst =
					currentCommunities.pages?.[0]?.data?.[0]?.uuid ===
					data.uuid;

				if (!communityIsFirst) {
					queryClient.invalidateQueries({
						queryKey: communityKeys.list('my_communities'),
					});
				}
			}
		}
	}, [data, queryClient]);

	return { community: data, isSuccess, isLoading, isError };
};

export const useGetPublicCommunity = (
	id: string,
	options: { enabled: boolean } = { enabled: true }
) => {
	const { api } = useServices();

	return useQuery<Community, BaseHttpError>({
		queryKey: communityKeys.publicDetail(id),
		queryFn: () => api.getCommunity(id).then(response => response.data),
		enabled: !!id && options.enabled,
		select: selectCommunity,
	});
};
